import RenderManager from "../rendering/render-manager";
import Network from "../miniviews/network";
import { calcAnimated } from "../slides/animation/tweenAnimation";
import { Tween } from "./camera-tweens/base-tween";
import { KeyframeTweenZoom } from "./camera-tweens/tween-zoom";
import { KeyframeTweenProjection } from "./camera-tweens/tween-projection";
import { KeyframeTweenTransform } from "./camera-tweens/tween-transform";
import { KeyframeTweenWorld } from "./camera-tweens/tween-world";
import SlideManager from "../utils/slide-manager";
import CameraTweenTrack from "./camera-tween-track";

function updateCamera(onCameraTransitionComplete) {

    /**
     * @param {SlideSettings} slide
     */
    function getTweens(slide) {
        const gate = Network.getWorld(slide.gate);

        if (!gate) return [];

        /**
         * @type {Tween[]}
         */
        const tweens = [];

        const paths = Network.depthFirstSearch(Network.getWorld(Network.currentWorldUuid), gate);
        const countPaths = paths.length;

        const currentKeyframeZoom = new KeyframeTweenZoom({
            ease: RenderManager.activeCamera.ease,
            zoom: RenderManager.activeCamera.zoom
        });

        const currentKeyframeProjection = new KeyframeTweenProjection({
            near: RenderManager.activeCamera.near,
            far: RenderManager.activeCamera.far,
            fov: RenderManager.activeCamera.fov
        });

        const desiredKeyframeZoom = new KeyframeTweenZoom().fromSlide(slide);
        const desiredKeyframeProjection = new KeyframeTweenProjection().fromSlide(slide);

        if (countPaths === 0) {

            const currentKeyframeTransform = new KeyframeTweenTransform({
                position: RenderManager.activeCamera.position,
                target: Q3.controls ? Q3.controls.target : new THREE.Vector3()
            });

            const desiredKeyframeTransform = new KeyframeTweenTransform().fromSlide(slide);

            tweens.push(
                new Tween(currentKeyframeTransform, desiredKeyframeTransform),
                new Tween(currentKeyframeZoom, desiredKeyframeZoom),
                new Tween(currentKeyframeProjection, desiredKeyframeProjection)
            );

        } else {

            let lastWorld = Network.currentWorldUuid;
            const lastPosition = new THREE.Vector3().copy(RenderManager.activeCamera.position);
            const lastTarget = new THREE.Vector3().copy(Q3.controls.target);

            const currentPath = paths[countPaths - 1];
            const { uuid: worldUuid, out: isExiting } = (currentPath || { uuid: lastWorld, out: true });
            const currentWorld = Network.getWorld(worldUuid);
            const currentGate = currentWorld.gate;

            const lastGate = Network.getWorld(lastWorld).gate;

            const sOrigin = new THREE.Vector3();
            let sScale = 1, sRadius = 100;

            if (isExiting) {

                const animated = calcAnimated(lastGate, slide.slideId);
                sOrigin.copy(animated.position);
                sScale = animated.scale.x;


                sRadius = lastGate.boundingRadius;
            } else {
                sOrigin.copy(currentGate.position);
                // sScale = currentGate.scale.x;
                sScale = 1;
                sRadius = currentGate.boundingRadius;
            }

            const [p1, p2] = isExiting
                ? Network.calcOutsideWorld({
                    sOrigin,
                    sScale,
                    sRadius
                }, lastPosition, lastTarget)
                : Network.calcInsideWorld({
                    sOrigin,
                    sScale,
                    sRadius
                }, lastPosition, lastTarget);

            const currentKeyframeTransform = new KeyframeTweenTransform({
                position: p1,
                target: p2
            });

            const desiredKeyframeTransform = new KeyframeTweenTransform().fromSlide(slide);

            const currentWorldTween = new KeyframeTweenWorld({ world: lastWorld });
            const desiredWorldTween = new KeyframeTweenWorld({ world: worldUuid });
            tweens.push(
                new Tween(currentWorldTween, desiredWorldTween),
                new Tween(currentKeyframeTransform, desiredKeyframeTransform),
                new Tween(currentKeyframeProjection, desiredKeyframeProjection),
            );
        }


        return tweens;
    }

    RenderManager.activeCamera.updateCamera = ({
        slideNumber,
        onTransitionComplete,
        duration = 2.5
    } = {}) => {
        if (Q3.controls.isStaticControls) return;

        CameraTweenTrack.killTweens();

        slideNumber = typeof slideNumber === "number" ? slideNumber : SlideManager.activeSlide;

        const slide = Q3.data.slides[slideNumber];
        const slideSettings = new SlideSettings(slideNumber, slide, duration);
        const timeline = CameraTweenTrack.activeTimeline = new TimelineLite({ paused: true });

        timeline.add(onStartTimeline, 0);

        if (!window.editAssetMode && slideSettings.cameraType !== "asset") {
            getTweens(slideSettings).forEach(tween => {
                const _tween = tween.keyframeBegin.tweenTo(tween.keyframeGoal, Math.max(slideSettings.time, 1e-5));
                timeline.add(_tween, 1e-5);
            });
        }

        timeline.add(onCompleteTimeline.bind(undefined, SlideManager.activeSlide));

        if (onTransitionComplete) timeline.add(onTransitionComplete);
        if (onCameraTransitionComplete) timeline.add(onCameraTransitionComplete);

        timeline.play();
    };
}

function getCameraObject(slideIndex) {
    const slide = typeof slideIndex !== undefined
        ? SlideManager.getSlide(slideIndex)
        : SlideManager.currentSlide;

    const isMobile = Q3.browserCapabilities.isMobile;
    const camData = Object.assign({}, slide.camera);

    if (slide.camera.sizeSm && ((Q3.editMode && Q3.mobileView) || (!Q3.editMode && (isMobile || window.location.pathname.indexOf("/view/preview") !== -1) && window.innerWidth < 768))) {
        Object.assign(camData, slide.camera.sizeSm);
    }

    return camData;
}

function onStartTimeline() {
    Q3.controls.tweening = true;

    Q3.labels.live.setLocked(true);

    if (Q3.editMode) Q3.undoService.setLocked(true);
}

function onCompleteTimeline(slideIndex) {
    const slideCamera = getCameraObject(slideIndex);

    RenderManager.activeCamera.position.copy(slideCamera.position);

    Q3.controls.target.copy(slideCamera.target);
    Q3.controls.tweening = false;

    Q3.labels.live.clearLast();

    RenderManager.enqueRaycaster();

    Q3.labels.live.setLocked(false);

    if (Q3.editMode) Q3.undoService.setLocked(false);

    CameraTweenTrack.activeTimeline = null;
}

// ----------------------------------------
//                  SLIDE
// ----------------------------------------
class SlideSettings {
    constructor(slideId, slide, duration = 4) {
        this.slideId = slideId;

        const slideCamera = getCameraObject(slideId);

        this.cameraType = SlideManager.getSlide(slideId).controls.interactionType;

        this.position = new THREE.Vector3().copy(slideCamera.position);
        this.target = new THREE.Vector3().copy(slideCamera.target);

        /**
         * @type {number}
         */
        this.time = duration;
        /**
         * @type {number}
         */
        this.zoom = slide.camera.zoom || 1;
        /**
         * @type {number}
         */
        this.near = slide.camera.far || RenderManager.activeCamera.near;
        /**
         * @type {number}
         */
        this.far = slide.camera.near || RenderManager.activeCamera.far;
        /**
         * @type {number}
         */
        this.fov = slide.camera.fov || Q3.data.setups.camera.fov;
        /**
         * @type {string}
         */
        this.gate = slide.camera.gate;

    }
}

export default updateCamera;
export { updateCamera };